import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { todayISO } from '../../lib/format';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, field } from '../create/Field';
import { money } from '../money';

/**
 * Recording money that arrived (V5 dogfood — `payment.recordManual` existed only on the
 * TECH's phone, so a cheque that came in the post, a bank transfer, or a card taken over
 * the counter could not be recorded by the office at all. For this ICP that is how most
 * invoices actually get paid).
 *
 * The amount defaults to the full balance because paying in full is the common case, and
 * a part payment is a deliberate edit rather than a number somebody has to retype.
 */

const METHODS: { key: 'check' | 'cash' | 'other'; label: string }[] = [
  { key: 'check', label: 'Cheque' },
  { key: 'cash', label: 'Cash' },
  { key: 'other', label: 'Bank transfer / other' },
];

export function RecordPaymentCard({
  invoiceId,
  balanceCents,
  tz,
}: {
  invoiceId: string;
  balanceCents: number;
  /** The shop's zone — what date "today" pre-fills as. */
  tz: string;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState((balanceCents / 100).toFixed(2));
  const [method, setMethod] = useState<'check' | 'cash' | 'other'>('check');
  const [reference, setReference] = useState('');
  // Defaulted to the UTC date, so a payment taken at 9 PM on the 31st in New York
  // pre-filled as the 1st — landing in next month's revenue unless the person noticed and
  // corrected it. The input side of exactly what `rangeWhere` fixes on the read side.
  const [receivedAt, setReceivedAt] = useState(todayISO(tz));

  const record = useMutation(
    trpc.payment.recordManual.mutationOptions({
      onSuccess: () => {
        setOpen(false);
        setReference('');
        void qc.invalidateQueries();
      },
    }),
  );

  const cents = Math.round(Number(amount.replace(/[$,\s]/g, '')) * 100);
  const valid = Number.isFinite(cents) && cents > 0;
  const over = valid && cents > balanceCents;

  if (!open) {
    return (
      <SectionCard title="Getting paid">
        <p className="mb-2 text-sm text-muted-foreground">
          {money(balanceCents)} still owed. Record a cheque, cash or a bank transfer when
          it lands.
        </p>
        <Button onClick={() => setOpen(true)}>Record a payment</Button>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Record a payment">
      {record.error ? <Notice tone="danger">{record.error.message}</Notice> : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="How much">
          <input
            className={field}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            autoFocus
          />
        </Field>
        <Field label="How it came">
          <select
            className={field}
            value={method}
            onChange={(e) => setMethod(e.target.value as 'check' | 'cash' | 'other')}
          >
            {METHODS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="When it arrived">
          <input
            className={field}
            type="date"
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
          />
        </Field>
        <Field label={method === 'check' ? 'Cheque number' : 'Reference'}>
          <input
            className={field}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={method === 'check' ? '4417' : 'Anything that helps you find it'}
          />
        </Field>
      </div>

      {/* Overpayment is allowed — customers round up, and refusing the entry would just
          send someone to a spreadsheet. It is said out loud so it is never a surprise. */}
      {over ? (
        <div className="mt-2">
          <Notice tone="warn">
            That's {money(cents - balanceCents)} more than the {money(balanceCents)} owed.
            Recording it will leave the invoice overpaid.
          </Notice>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          disabled={!valid || record.isPending}
          onClick={() =>
            record.mutate({
              invoiceId,
              amountCents: cents,
              method,
              receivedAt: new Date(`${receivedAt}T12:00:00`),
              reference: reference.trim() || undefined,
              ...idem(),
            })
          }
        >
          {record.isPending ? 'Recording…' : `Record ${money(valid ? cents : 0)}`}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Never mind
        </Button>
      </div>
    </SectionCard>
  );
}
